import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
    get as _get,
    isEqual as _isEqual,
    cloneDeep as _cloneDeep,
} from 'lodash';

import { makeStyles } from '@mui/styles';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Collapse from '@mui/material/Collapse';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';

import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/MoreVert';
import HelpIcon from '@mui/icons-material/Help';
import CloseIcon from '@mui/icons-material/Close';

import DataContext from '../../data/data-context';
import Auth from '../../data/auth-service';

const drawerWidth = '300px';
const drawerWidthCollapsed = '60px !important';

const useStyles = makeStyles(theme => ({
    drawer: {
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        backgroundColor: theme.palette.nav.paper,
        color: theme.palette.nav.text,
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
        transition: 'width 500ms',
    },
    drawerCollapsed: {
        width: drawerWidthCollapsed,
        transition: 'width 500ms',
    },
    drawerPaper: {
        width: drawerWidth,
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
        marginLeft: '60px',
        background: theme.palette.nav.paper,
        transition: 'margin-left 500ms, width 500ms',
    },
    drawerPaperExtended: {
        marginLeft: '300px',
        transition: 'margin-left 500ms, width 500ms',
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: theme.spacing(0, 1),
        minHeight: 85,
        ...theme.mixins.toolbar,
    },
    hightlightPrimary: {
        color: theme.palette.nav.primary,
    },
    hightlightSecondary: {
        color: theme.palette.secondary.main,
    },
    list: {
        flex: 1,
        width: '100%',
        minWidth: 275,
        maxWidth: 300,
        marginTop: 12,
        color: theme.palette.nav.text,
        '& .MuiListItem-button': {
            borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
            paddingTop: '10px',
            paddingBottom: '10px',
            minHeight: 53,
            '& div.MuiListItemIcon-root': {
                color: theme.palette.nav.text,
            }
        },
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
    },
    listItem: {
        color: theme.palette.nav.text,
    },
    statusListItem: {
        padding: '0px 16px',
    },
    listItemPadding: {
        minWidth: '16px',
        width: '16px',
    },
    policyColor: {
        width: '20px',
        height: '20px',
        marginRight: '10px',
    },
    customIcon: {
        width: 22,
        height: 22,
    },
    policyText: {
        cursor: 'pointer',
        whiteSpace: 'break-spaces',
    },
    policyTextPrimary: {
        fontSize: '14px !important',
    },
    policyTextSecondary: {
        fontSize: '12px !important',
        color: theme.palette.nav.text,
    },
    PolicySecondaryAction: {
        color: theme.palette.nav.text,
    },
    status: {
        height: '20px',
        width: '20px',
        borderRadius: '20px',
        marginRight: theme.spacing(1),
        marginTop: '10px',
    },
    good: {
        background: theme.palette.success.main,
    },
    ok: {
        background: theme.palette.warning.main,
    },
    bad: {
        background: theme.palette.error.main,
    },
    textFieldBorderFix: {
        color: '#FFF',
        '& label': {
            color: '#FFF',
        },
        '& label.Mui-focused': {
            color: '#FFF',
        },
        '& .MuiInput-underline:after': {
            borderBottomColor: 'rgba(255, 255, 255, 0.25)',
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.25)',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.25)',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.25)',
            },
        },
    },
    inputFix: {
        color: '#FFF',
        borderColor: '#FFF',
    },
    iconButtonFix: {
        color: '#FFF',
    }
}));

const SORT_TYPES = [
    { value: 'status', name: 'Status' },
    { value: 'name', name: 'Name' },
    { value: 'host', name: 'Host' },
];

const CMCSidebar = (props) => {
    const classes = useStyles();
    // const [policyInterval, setPolicyInterval] = useState(null);
    const [sortAnchor, setSortAnchor] = useState(null);
    const [readonly] = useState(!Auth.perm('update-cmc'));
    const [menuAnchor, setMenuAnchor] = useState(null);
    const { systemSettings, sidenav, setSidenav } = useContext(DataContext);
    const [panels, setPanels] = useState({
        suricata: false,
        zeek: false,
        bricata: false,
    });
    const [selectedPolicy, setSelectedPolicy] = useState({ type: '', name: '' });
    const [policies, setPolicies] = useState({
        suricata: [],
        zeek: [],
        bricata: [],
    });
    const [header, setHeader] = useState();
    const [filter, setFilter] = useState(_cloneDeep(props.filter));
    const [searchTypeMenu, setSearchTypeMenu] = useState(null);

    // sidenav uses the updateReducer which does a merge on update
    useEffect(() => {
        setSidenav({ sidebar: 'open' });

        return () => {
            setSidenav({ sidebar: undefined });
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setPolicies({
            bricata: props.policies.filter(v =>
                !v.name.toLowerCase().includes('bt') &&
                !v.name.toLowerCase().includes('upgrade') &&
                !v.name.toLowerCase().includes('suricata') &&
                !v.name.toLowerCase().includes('zeek')
            ),
            suricata: props.policies.filter(v =>
                !v.name.toLowerCase().includes('bt') &&
                v.name.toLowerCase().includes('suricata')
            ),
            zeek: props.policies.filter(v =>
                !v.name.toLowerCase().includes('bt') &&
                v.name.toLowerCase().includes('zeek')
            ),
        });
    }, [props]);

    useEffect(() => {
        if (!_isEqual(filter, props.filter)) {
            props.onUpdateFilter(filter);
        }
    }, [filter, props]);

    useEffect(() => {
        props.onUpdateFilter(props.filter);
        setFilter(props.filter);
    }, [props, props.filter]);

    useEffect(() => {
        setHeader(!!_get(systemSettings, 'ui.messages.header'));
    }, [systemSettings]);

    const handleNavCollapse = b => {
        setSidenav({
            sidebar: b ? 'collapsed' : 'open',
        });
        setPanels({});
    };

    const handleOpenPolicySidebarMenu = (e, type, name) => {
        setSelectedPolicy({ type: type, name: name });
        setMenuAnchor(e.currentTarget);
    };

    const handleClosePolicySudebarMenu = () => {
        setSelectedPolicy({ type: '', name: '' });
        setMenuAnchor(null);
    };

    const handleSelectPolicy = (type, name) => {
        props.onSelectPolicy(type, name);
    };

    const handleAddToFiltered = () => {
        props.onAddToFiltered(selectedPolicy);
        setMenuAnchor(null);
    };

    const handleAddToAll = () => {
        props.onAddToAll(selectedPolicy);
        setMenuAnchor(null);
    };

    const handleUpdatePanels = (menu, submenu) => {
        if (menu) {
            setSidenav({ sidebar: 'open' });
        }
        if (submenu) {
            setPanels({
                [menu]: true,
                [submenu]: !panels[submenu],
            });
        } else {
            setPanels({
                [menu]: !panels[menu],
            });
        }
    };

    const handleOpenSort = (e) => {
        setSortAnchor(e.target);
    };

    const handleCloseSort = () => {
        setSortAnchor('');
    };

    const handleUpdateSort = (s) => {
        let d = 'desc';

        if (filter.sort.split('+')[0] === s) {
            d = (filter.sort.split('+')[1] === 'desc' ? 'asc' : 'desc');
        }

        setFilter({
            ...filter,
            sort: `${s}+${d}`,
        });
        setSortAnchor(null);
    };

    const handleUpdateStatus = (e, s) => {
        const f = _cloneDeep(filter);
        f.status[s] = e.target.checked;
        setFilter(f);
    };

    const handleOpenSearchTypeMenu = (e) => {
        setSearchTypeMenu(e.target);
    };

    const handleCloseSearchTypeMenu = () => {
        setSearchTypeMenu(null);
    };

    const handleUpdateSearchType = (value) => {
        setFilter({
            ...filter,
            type: value,
        });
        handleCloseSearchTypeMenu();
    };

    const handleClearSearch = () => {
        setFilter({
            ...filter,
            q: '',
        });
    };

    const handleUpdateSearch = (e) => {
        setFilter({
            ...filter,
            q: e.target.value,
        });
    };

    return (
        <Drawer
            open={true}
            className={clsx({
                [classes.drawer]: true,
                [classes.drawerCollapsed]: open.collapsed,
            })}
            variant="persistent"
            classes={{
                paper: clsx({
                    [classes.drawerPaper]: true,
                    [classes.drawerCollapsed]: sidenav.sidebar === 'collapsed',
                    [classes.drawerPaperExtended]: !sidenav.collapsed,
                }),
            }}
        >
            <div className={classes.toolbar} style={{ marginTop: header ? 30 : 0 }}></div>
            <List component="nav" className={classes.list} aria-label="Side Nav Bar">
                <ListItem button
                    onClick={() => { handleNavCollapse(sidenav.sidebar !== 'collapsed') }}
                    aria-label="Expand or Collpase Side Policy Bar">
                    <ListItemIcon>
                        {/* {sidenav.sidebar === 'collapsed' ? <NavigateNextIcon onClick={(e) => { handleNavCollapse(false) }} /> : <NavigateBeforeIcon onClick={(e) => { handleNavCollapse(true) }} />} */}
                        {sidenav.sidebar === 'collapsed' ? <NavigateNextIcon/> : <NavigateBeforeIcon/>}
                    </ListItemIcon>
                    <ListItemSecondaryAction>
                        <Tooltip
                            title="Add CMCs by using the menu options. It may take up to a minute to reflect the changes. Group affiliation count may be inaccurate for CMCs that are offline."
                        ><HelpIcon /></Tooltip>
                    </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                    <ListItemText>
                        <Box display={sidenav.sidebar === 'collapsed' ? 'none' : 'flex'} justifyContent="space-between">
                            <Box>Filters</Box>
                            <Box>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    aria-label="filters"
                                    onClick={handleOpenSearchTypeMenu}
                                >
                                    {filter.type}
                                </Button>
                                <Menu
                                    open={Boolean(searchTypeMenu)}
                                    anchorEl={searchTypeMenu}
                                    onClose={handleCloseSearchTypeMenu}
                                >
                                    <MenuItem aria-label="filter all" onClick={() => { handleUpdateSearchType('All') }}>All</MenuItem>
                                    <MenuItem aria-label="filter cmcs" onClick={() => { handleUpdateSearchType('CMCs') }}>CMCs</MenuItem>
                                    <MenuItem aria-label="filter sensors" onClick={() => { handleUpdateSearchType('Sensors') }}>Sensors</MenuItem>
                                </Menu>
                            </Box>
                        </Box>
                    </ListItemText>
                </ListItem>
                <Collapse in={sidenav.sidebar !== 'collapsed'} timeout="auto" unmountOnExit>
                    <List>
                        <ListItemButton className={classes.listItem}>
                            <TextField
                                fullWidth
                                label="Search"
                                aria-label="search"
                                onChange={handleUpdateSearch}
                                value={filter.q}
                                className={classes.textFieldBorderFix}
                                InputProps={{
                                    className: classes.inputFix,
                                    endAdornment: <IconButton
                                        className={classes.iconButtonFix}
                                        size="small"
                                        onClick={handleClearSearch}
                                        disabled={!filter.q.length}
                                        aria-label="Clear search"
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                }}
                            />
                        </ListItemButton>
                        <ListItemButton className={classes.listItem} onClick={handleOpenSort} aria-label="Open sort menu">
                            <Box display="flex" width="100%" justifyContent="space-between" mt={1}>
                                <Box>Sort</Box>
                                <Box display="flex">
                                    {(() => {
                                        const s = filter.sort.split('+')[0];
                                        return s[0].toUpperCase() + s.substr(1);
                                    })()}
                                    <Box mt={-0.5}>{filter.sort.split('+')[1] === 'desc' ? <ExpandMoreIcon /> : <ExpandLessIcon />}</Box>
                                </Box>
                            </Box>
                            <Menu
                                anchorEl={sortAnchor}
                                open={Boolean(sortAnchor)}
                                onClose={handleCloseSort}
                                onClick={e => { e.stopPropagation() }}
                            >
                                {SORT_TYPES.map((v, i) => {
                                    return (
                                        <MenuItem
                                            key={'sort-menu-item-' + i}
                                            className={classes.menuItem}
                                            onClick={() => { handleUpdateSort(v.value) }}
                                        >
                                            <Box display="flex" width="100%">{v.name}</Box>
                                        </MenuItem>
                                    );
                                })}
                            </Menu>
                        </ListItemButton>
                        <ListItemButton className={classes.listItem}>
                            <Box>
                                Status
                                <List>
                                    <ListItem className={classes.statusListItem}>
                                        <Box display="flex">
                                            <Box className={clsx({
                                                [classes.status]: true,
                                                [classes.good]: true,
                                            })}></Box>
                                            <Box>
                                                <FormControlLabel
                                                    label="Normal"
                                                    labelPlacement="end"
                                                    checked={filter.status.normal}
                                                    control={<Checkbox onClick={e => { handleUpdateStatus(e, 'normal') }} />}
                                                />
                                            </Box>
                                        </Box>
                                    </ListItem>
                                    <ListItem className={classes.statusListItem}>
                                        <Box display="flex">
                                            <Box className={clsx({
                                                [classes.status]: true,
                                                [classes.ok]: true,
                                            })}></Box>
                                            <Box>
                                                <FormControlLabel
                                                    label="Warning"
                                                    labelPlacement="end"
                                                    checked={filter.status.warning}
                                                    control={<Checkbox onClick={e => { handleUpdateStatus(e, 'warning') }} />}
                                                />
                                            </Box>
                                        </Box>
                                    </ListItem>
                                    <ListItem className={classes.statusListItem}>
                                        <Box display="flex">
                                            <Box className={clsx({
                                                [classes.status]: true,
                                                [classes.bad]: true,
                                            })}></Box>
                                            <Box>
                                                <FormControlLabel
                                                    label="Critical"
                                                    labelPlacement="end"
                                                    checked={filter.status.critical}
                                                    control={<Checkbox onClick={e => { handleUpdateStatus(e, 'critical') }} />}
                                                />
                                            </Box>
                                        </Box>
                                    </ListItem>
                                </List>
                            </Box>
                        </ListItemButton>
                    </List>
                </Collapse>
                <ListItem>
                    <ListItemText><Box display={sidenav.sidebar === 'collapsed' ? 'none' : 'flex'}>Policies</Box></ListItemText>
                </ListItem>
                <Box pt={'5px'} mb={25}>
                    {Object.keys(policies).map((x, i) => {
                        return (
                            <React.Fragment key={'policy-sidebar-group-' + i}>
                                <ListItemButton onClick={(e) => { handleUpdatePanels(x) }} aria-label={'Expand or Collapse ' + x + ' sub-menu'}>
                                    <ListItemIcon>
                                        {(() => {
                                            if (x.includes('suricata')) {
                                                return <img className={classes.customIcon} src="/images/icon-suricata.png" />;
                                            } else if (x.includes('zeek')) {
                                                return <img className={classes.customIcon} src="/images/icon-zeek.png" />;
                                            } else {
                                                return <img className={classes.customIcon} src="/images/icon-new.png" />;
                                            }
                                        })()}
                                    </ListItemIcon>
                                    <ListItemText
                                        style={{ display: (open.collapsed ? 'none' : 'block') }}
                                        primary={x === 'bricata' ? 'OpenText™ NDR' : x[0].toUpperCase() + x.substring(1)}
                                    />
                                    {panels[x] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </ListItemButton>
                                <Collapse in={panels[x]} timeout="auto" unmountOnExit>
                                    <List>
                                        {policies[x] && policies[x].map((v, i) => {
                                            return (
                                                <React.Fragment key={'policy-sidebar-item-' + i}>
                                                    <ListItem button className={classes.listItem} onClick={(e) => { handleUpdatePanels(x, v.type) }} aria-label={'Expand or collpase ' + v.name + ' sub-menu'}>
                                                        <ListItemIcon></ListItemIcon>
                                                        <ListItemText className={classes.hightlightPrimary} primary={v.name} />
                                                        {panels[v.type] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    </ListItem>
                                                    <Collapse in={panels[v.type]} timeout="auto" unmountOnExit>
                                                        <List>
                                                            {v.policies.length ? v.policies.map((y, j) => {
                                                                return (
                                                                    <ListItem key={'policy-subitem-' + j}>
                                                                        <ListItemIcon></ListItemIcon>
                                                                        <ListItemIcon className={classes.listItemPadding}></ListItemIcon>
                                                                        <Avatar className={classes.policyColor} style={{ background: y.color || '#777' }}>{''}</Avatar>
                                                                        <ListItemText
                                                                            className={classes.policyText}
                                                                            classes={{
                                                                                primary: classes.policyTextPrimary,
                                                                                secondary: classes.policyTextSecondary,
                                                                            }}
                                                                            primary={y.name}
                                                                            secondary={'Sensors: ' + y.count}
                                                                            onClick={() => { handleSelectPolicy(v.type, y.name) }}
                                                                        />
                                                                        {!readonly && <ListItemSecondaryAction onClick={(e) => { handleOpenPolicySidebarMenu(e, v.type, y.name) }}>
                                                                            <IconButton
                                                                                aria-label="policy secondary action"
                                                                                className={classes.PolicySecondaryAction}
                                                                                size="large"><MenuIcon /></IconButton>
                                                                        </ListItemSecondaryAction>}
                                                                    </ListItem>
                                                                );
                                                            }) : <ListItem>
                                                                <ListItemIcon></ListItemIcon>
                                                                <ListItemText
                                                                    className={classes.policyText}
                                                                    classes={{
                                                                        primary: classes.policyTextPrimary,
                                                                        secondary: classes.policyTextSecondary,
                                                                    }}
                                                                    secondary={'No policies created...'}
                                                                />
                                                            </ListItem>}
                                                        </List>
                                                    </Collapse>
                                                </React.Fragment>
                                            );
                                        })}
                                    </List>
                                </Collapse>
                            </React.Fragment>
                        );
                    })}
                </Box>
            </List>
            <Menu
                anchorEl={menuAnchor}
                keepMounted
                open={Boolean(menuAnchor)}
                onClose={handleClosePolicySudebarMenu}
            >
                <MenuItem onClick={handleAddToFiltered}>Add to CMCs and sensors in filter</MenuItem>
                <MenuItem onClick={handleAddToAll}>Add to all CMCs &amp; sensors</MenuItem>
            </Menu>
        </Drawer>
    );
};

CMCSidebar.propTypes = {
    policies: PropTypes.array.isRequired,
    filter: PropTypes.object.isRequired,
    onSelectPolicy: PropTypes.func.isRequired,
    onAddToAll: PropTypes.func.isRequired,
    onAddToFiltered: PropTypes.func.isRequired,
    onUpdateFilter: PropTypes.func.isRequired,
};

export default CMCSidebar;
